import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import SvgIcon from "./SvgIcon";
import { schoolAnalytics, schoolNotices, openApp, errorMessage } from "./api";
import "./principal-home.css";

// Home for a school principal (and for a teacher, for their own classes). Every number comes from the existing school analytics
// (GET /school/analytics), the school's licence (the apps list on the dashboard overview) and the school notice board
// (GET /school/notices). Nothing is made up: "Needs attention" is the students who have not started, plus those with no activity
// in the last 7 days, the same week window the analytics already uses for "active this week".

const APP_ICON = { quran: "read-quran", tajweed: "tajweed", understand: "understand-quran", qfc: "quran-fluency", poems: "children-poems", stories: "stories", hifz: "hifz", words: "read-quran", lms: "learning", living: "learning" };
const APP_LABEL = { stories: "Qur'anic Stories" };
const BUCKETS = [["not_started", "Not started"], ["1-25", "1 – 25%"], ["26-50", "26 – 50%"], ["51-75", "51 – 75%"], ["76-100", "76 – 100%"]];
const dateOf = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

export default function PrincipalHome({ data, token, go }) {
  const [a, setA] = useState(null);
  const [notices, setNotices] = useState(null);
  const [allNotices, setAllNotices] = useState(false);
  const [allClasses, setAllClasses] = useState(false);
  useEffect(() => { schoolAnalytics(token).then(setA).catch((e) => toast.error(errorMessage(e))); }, [token]);
  useEffect(() => { schoolNotices(token).then((r) => setNotices(r.notices || [])).catch(() => setNotices([])); }, [token]);

  const first = (data.user.name || "").split(" ")[0] || "there";
  const isPrincipal = !a || a.role === "principal";
  const apps = (data.apps || []).filter((x) => x.has_access && x.available);
  const max = a ? Math.max(1, ...Object.values(a.distribution || {})) : 1;
  const classes = (a && a.by_class) || [];
  const shownClasses = allClasses ? classes : classes.slice(0, 8);
  const attention = (a && a.attention) || [];
  const news = notices || [];
  const shownNews = allNotices ? news : news.slice(0, 2);
  const cards = a ? [
    { icon: "profiles", tone: "mint", value: a.total_students, label: "Students" },
    { icon: "progress", tone: "teal", value: a.active_students, label: "Active students" },
    { icon: "stats", tone: "gold", value: `${a.average_percent}%`, label: "Average progress" },
    { icon: "learning", tone: "mint", value: classes.length, label: "Classes" },
  ] : [];

  return (
    <div className="phome" data-testid="principal-home">
      <header className="phome-head">
        <div style={{ position: "relative", zIndex: 1, minWidth: 0 }}>
          <div className="phome-greet">As-salāmu ʿalaykum,</div>
          <h1 className="phome-hello" data-testid="principal-hello"><span>{isPrincipal ? (a ? a.school : data.school) || first : first}</span></h1>
          <p className="phome-sub">{isPrincipal ? `${data.user.name || ""} · Principal` : `${a ? a.school : data.school || ""} · Teacher`}</p>
        </div>
        <div className="phome-deco" aria-hidden="true">
          <img className="leaf l" src={`${process.env.PUBLIC_URL}/dashboard/leaves-left.png`} alt="" />
          <img className="mosque" src={`${process.env.PUBLIC_URL}/dashboard/mosque-header.png`} alt="" />
          <img className="leaf r" src={`${process.env.PUBLIC_URL}/dashboard/leaves-right.png`} alt="" />
        </div>
        <div className="phome-actions">
          <button type="button" className="phome-round" aria-label="Announcements" title="Announcements" data-testid="principal-bell"
            onClick={() => document.querySelector('[data-testid="principal-announcements"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}>
            <SvgIcon name="notifications" size={22} />
            {news.some((n) => !n.read) && <i className="phome-dot" aria-hidden="true" />}
          </button>
          <button type="button" className="phome-round" aria-label="My profile" title="My profile" onClick={() => go("myprofile")}><SvgIcon name="user" size={24} /></button>
        </div>
      </header>

      {!a ? <p className="phome-muted" style={{ marginTop: 24 }}>Loading your school…</p> : (<>
        <section className="phome-cards" aria-label="School summary" data-testid="principal-summary">
          {cards.map((c) => (
            <div className="phome-card phome-stat" key={c.label}>
              <span className={`phome-ico ${c.tone}`}><SvgIcon name={c.icon} size={30} /></span>
              <div><b>{c.value}</b><span>{c.label}</span></div>
            </div>
          ))}
        </section>

        <div className="phome-row3">
          <section className="phome-card" aria-label="Progress distribution" data-testid="principal-distribution">
            <h2 className="phome-title"><SvgIcon name="stats" size={22} /> Progress distribution</h2>
            <div className="phome-dist">
              {BUCKETS.map(([k, label]) => (
                <div key={k} className="phome-dist-row">
                  <span>{label}</span>
                  <div className="phome-bar"><i style={{ width: `${((a.distribution[k] || 0) / max) * 100}%` }} /></div>
                  <b>{a.distribution[k] || 0}</b>
                </div>
              ))}
            </div>
          </section>

          <section className="phome-card" aria-label="By class" data-testid="principal-classes">
            <div className="phome-title-row">
              <h2 className="phome-title"><SvgIcon name="learning" size={22} /> By class</h2>
              {classes.length > 8 && <button type="button" className="phome-link" onClick={() => setAllClasses((v) => !v)} aria-expanded={allClasses}>{allClasses ? "Show fewer" : "View all classes"} <SvgIcon name="arrow-right" size={15} /></button>}
              {classes.length <= 8 && classes.length > 0 && <button type="button" className="phome-link" onClick={() => go("students")}>View all classes <SvgIcon name="arrow-right" size={15} /></button>}
            </div>
            {classes.length === 0 ? <p className="phome-muted">No classes yet.</p> : (
              <div className="phome-classes">
                {shownClasses.map((c) => (
                  <div key={c.class} className="phome-class">
                    <span className="phome-class-ico"><SvgIcon name="profiles" size={18} /></span>
                    <div className="phome-class-main">
                      <div className="phome-class-top"><span>{c.class} · {c.students} student{c.students === 1 ? "" : "s"}</span><b>{c.average_percent}%</b></div>
                      <div className="phome-bar thin"><i style={{ width: `${Math.min(100, c.average_percent)}%` }} /></div>
                    </div>
                  </div>
                ))}
              </div>)}
          </section>

          <section className="phome-card phome-apps" aria-label="School apps" data-testid="principal-apps">
            <h2 className="phome-title"><SvgIcon name="learning" size={22} /> School Apps <span className="phome-faint">(Licence)</span></h2>
            {apps.length === 0 ? <p className="phome-muted">No apps are included in your licence yet.</p> : (
              <div className="phome-applist">
                {apps.map((x) => (
                  <button key={x.id} type="button" className="phome-app" onClick={() => openApp(x, token)} data-testid={`principal-app-${x.id}`}>
                    <span className="phome-appico"><SvgIcon name={APP_ICON[x.id] || "learning"} size={22} /></span>
                    <span className="phome-appname">{APP_LABEL[x.id] || x.name}</span>
                    <SvgIcon name="arrow-right" size={18} />
                  </button>
                ))}
              </div>)}
          </section>
        </div>

        <div className="phome-row2">
          <section className="phome-card" aria-label="Needs attention" data-testid="principal-attention">
            <div className="phome-title-row">
              <h2 className="phome-title"><span className="phome-warn" aria-hidden="true">!</span> Needs attention</h2>
              <button type="button" className="phome-link" onClick={() => go("students")} data-testid="principal-view-students">View students <SvgIcon name="arrow-right" size={15} /></button>
            </div>
            {attention.length === 0 ? <p className="phome-muted">Every student has started and been active in the last 7 days.</p> : (
              <div className="phome-att">
                {attention.slice(0, 4).map((s) => (
                  <button key={s.user_id} type="button" className="phome-att-row" onClick={() => go("students")}>
                    <span className="phome-class-ico lg"><SvgIcon name="user" size={22} /></span>
                    <span className="phome-att-main"><b>{s.name}</b><span>{s.class_name} {s.section}</span></span>
                    <span className={`phome-chip ${s.status}`}>{s.status === "not_started" ? "Not started" : "Inactive"}</span>
                    <SvgIcon name="chevron-right" size={18} />
                  </button>
                ))}
                {a.attention_total > 4 && <p className="phome-muted" style={{ margin: "6px 0 0" }}>and {a.attention_total - 4} more</p>}
              </div>)}
          </section>

          <section className="phome-card" aria-label="Announcements" data-testid="principal-announcements">
            <div className="phome-title-row">
              <h2 className="phome-title"><SvgIcon name="announcement" size={22} /> Announcements</h2>
              {news.length > 2 && <button type="button" className="phome-link" onClick={() => setAllNotices((v) => !v)} aria-expanded={allNotices}>{allNotices ? "Show less" : "View all"} <SvgIcon name="arrow-right" size={15} /></button>}
            </div>
            {shownNews.length === 0 ? (
              <div className="phome-empty">
                <span className="phome-ico mint big"><SvgIcon name="calendar" size={34} /></span>
                <div><b>You&apos;re all caught up.</b><p>New lessons and updates from HikmahVerses will appear here.</p></div>
              </div>
            ) : shownNews.map((n) => (
              <div className="phome-news" key={n.notice_id}>
                <span className="phome-ico mint"><SvgIcon name="announcement" size={22} /></span>
                <div><b>{n.title}</b><p>{n.body}</p><time>{dateOf(n.created_at)}</time></div>
              </div>
            ))}
          </section>
        </div>
      </>)}
    </div>
  );
}
